import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./AgentTable.css";
import { ArchiveIcon, ArrowCcwIcon, PenIcon } from "@xola/icons";
import { numberFormat, Spinner } from "@xola/ui-kit";
import { GridOptions, GridReadyEvent, RowClickedEvent } from "ag-grid-community";
import { ColDef, GridApi, LicenseManager } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { useInViewport } from "ahooks";
import clsx from "clsx";
import { Loader } from "common";
import React, { useMemo, useRef, useState } from "react";
import { useMutation } from "react-query";
import { useHistory } from "react-router-dom";
import {
    Affiliate,
    AffiliateStatus,
    CouponAlgorithm,
    LineItemType,
    PartnerObject,
    Permission,
    PurchaseRule,
    PurchaseRuleLink,
    PurchaseRuleLinkEntity,
    TagId,
    TermsType,
} from "types";
import { api } from "../../api/api";
import { config } from "../../config";
import { extractNumericPart } from "../../helpers/mathHelper";
import { useSeller, useUser } from "../../hooks/useAccount";
import { useCollection } from "../../hooks/useCollection";
import { useInvalidate } from "../../hooks/useInvalidation";
import { getPurchaseRulesByTag } from "../../models/PurchaseRule";
import { getAlgorithm, getAmount, getTermsType } from "../../models/TermsTemplatePurchaseRule";
import { isAffiliate } from "../../models/User";
import { PromptModal } from "../Common/PromptModal";
import { SecondaryButton } from "../Common/SecondaryButton";
import { ErrorBoundary } from "../ErrorBoundary";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { useAuthorization } from "@/hooks/useAuthorization";
import { useAutoFetchCollection } from "@/hooks/useAutoFetchCollection";

export interface CustomAffiliate extends Affiliate {
    agent: CustomAffiliate;
    __children?: Affiliate[] | string[];
}

export enum AgentOption {
    ID = "Id",
    PARTNER = "Agent",
    TEMPLATE = "Template",
    CODE = "Code",
    COMMISSION = "Commission",
    DISCOUNT = "Discount",
    ACTION = "Action",
}

interface AgentTableProps {
    agents: any[];
    onSelectAgents: (selectedAgents: string[]) => void;
    onArchiveAgent: (agent: Affiliate) => void;
    onLinkClick: (route: string) => void;
}

const defaultColDef: ColDef = {
    resizable: false,
    sortable: false, // Suppress sorting
    filter: false,
    menuTabs: [],
    suppressMovable: true,
    suppressAutoSize: true,
    flex: 1,
};

export const AgentTable = ({ agents, onSelectAgents, onLinkClick, onArchiveAgent }: AgentTableProps) => {
    const { isGranted } = useAuthorization();
    const user = useUser();

    const loggedInAffiliate = agents.find((agent) => agent?.user?.id === user.id);

    const gridApi = useRef<GridApi | null>(null);
    // Create a map of child ID to parent ID
    const childToParentMap: { [key: ID]: ID } = {};
    for (const agent of agents) {
        if (agent.children) {
            for (const childId of agent.children) {
                childToParentMap[childId] = agent.id;
            }
        }
    }

    // Adjust the getDataPath function
    const getDataPath = (data: any) => {
        const path = [];
        let currentId = data.id;
        while (currentId) {
            path.unshift(currentId);
            currentId = childToParentMap[currentId];
        }

        return path;
    };

    const onGridReady = (params: GridReadyEvent) => {
        // Save the API and column API for later use
        gridApi.current = params.api;
    };

    const onSelectionChanged = () => {
        const selectedNodes = gridApi.current?.getSelectedNodes();
        // Only include nodes that are selectable (group or leaf, as per checkboxSelection)
        const selectedData = selectedNodes?.map((node) => node.data);
        if (selectedData) {
            onSelectAgents(selectedData.map((data) => data.id));
        }
    };

    const columnDefs = [
        { ...defaultColDef, field: "code", width: 160, cellRenderer: "codeRenderer" },
        {
            ...defaultColDef,
            headerName: "My Terms Template",
            field: "purchaseRuleLink",
            cellRenderer: "termsTemplateRenderer",
            minWidth: 300,
            cellClass: "my-term-template-cell",
        },
        {
            ...defaultColDef,
            field: "commission",
            width: 200,
            cellRenderer: "commissionAndDiscountCellRenderer",
            cellClass: "no-translate",
            cellRendererParams: { lineItemType: LineItemType.AFFILIATE_COMMISSION },
        },
        {
            ...defaultColDef,
            field: "discount",
            width: 200,
            cellRenderer: "commissionAndDiscountCellRenderer",
            cellRendererParams: { lineItemType: LineItemType.AFFILIATE_DISCOUNT },
        },
        {
            ...defaultColDef,
            field: "actions",
            width: 160,
            cellRenderer: "actionsCellRenderer",
            hide: !isGranted(Permission.AFFILIATES_ACTIONS),
            cellRendererParams: {
                onArchiveAgent: onArchiveAgent,
                onLinkClick: onLinkClick,
            },
        },
    ];

    const gridOptions: GridOptions = {
        onSelectionChanged: onSelectionChanged,
        rowSelection: "multiple",
        enableCellTextSelection: true,
        ensureDomOrder: true,
    };

    const components = {
        actionsCellRenderer: ActionsCellRenderer,
        codeRenderer: CodeRenderer,
        commissionAndDiscountCellRenderer: CommissionAndDiscountCellRenderer,
        nameCellRenderer: NameCellRenderer,
        termsTemplateRenderer: TermsTemplateRenderer,
    };

    return (
        <ErrorBoundary>
            <div className="ag-theme-alpine ui-agent-table" style={{ height: "100vh" }}>
                <AgGridReact
                    treeData
                    suppressCellFocus
                    suppressContextMenu
                    theme="legacy"
                    groupSelectsChildren={false}
                    groupDisplayType={undefined}
                    groupDefaultExpanded={1} // Auto-open parent affiliates
                    autoGroupColumnDef={{
                        ...defaultColDef,
                        headerName: AgentOption.PARTNER,
                        field: "name",
                        checkboxSelection: !!isGranted(Permission.AFFILIATES_ACTIONS) && !loggedInAffiliate,
                        headerCheckboxSelection: false,

                        cellRenderer: "agGroupCellRenderer",
                        cellRendererParams: {
                            innerRenderer: "nameCellRenderer",
                            suppressCount: true,
                        },
                        minWidth: 200,
                        cellClass: "agent-table-name-cell no-translate",
                    }}
                    rowData={agents}
                    columnDefs={columnDefs}
                    getDataPath={getDataPath}
                    components={components}
                    gridOptions={gridOptions}
                    onRowClicked={handleRowClick}
                    onGridReady={onGridReady}
                    onSelectionChanged={onSelectionChanged}
                />
            </div>
        </ErrorBoundary>
    );
};

const handleRowClick = (event: RowClickedEvent) => {
    // Toggle row expand/collapse
    event.node.setExpanded(!event.node.expanded);
};

interface ActionsCellRendererProps {
    data: Affiliate & { __children?: string[] };
    onArchiveAgent: (agent: Affiliate) => void;
    onLinkClick: (route: string) => void;
}

const ActionsCellRenderer = ({ data, onArchiveAgent, onLinkClick }: ActionsCellRendererProps) => {
    const [showConfirmationModal, setShowConfirmationModal] = useState(false);
    const seller = useSeller();
    const user = useUser();
    const invalidate = useInvalidate();

    // If it is an affiliate logged in, allow editing children if allowChildren = true
    const {
        data: [loggedInAffiliate],
        isLoading,
    } = useCollection<Affiliate>(
        "affiliates",
        { user: user.id, limit: 1, status: "active" },
        { enabled: isAffiliate(user) },
    );

    const isArchived = data?.status !== AffiliateStatus.ACTIVE;
    const hasChildren = data?.__children && data.__children.length > 0;
    const canEditAgent = isAffiliate(user) ? user.id === data?.user?.id || loggedInAffiliate?.allowChildren : true;
    const canArchiveAgent = isAffiliate(user) ? loggedInAffiliate?.allowChildren && user.id !== data?.user?.id : true;

    const { mutateAsync: archive } = useMutation(async (agent: Affiliate) => {
        const { data } = await api.put<Affiliate>(`/users/${seller.id}/affiliates/${agent.id}`, agent);
        return data;
    });

    const handleArchive = async (status: AffiliateStatus) => {
        const agent = await archive({ ...data, status });
        onArchiveAgent(agent);
        invalidate("affiliates", data.id);
        invalidate("partners", data.id);
    };

    return isLoading ? (
        <Loader size="tiny" />
    ) : (
        <span className="space-x-1">
            {canEditAgent ? (
                <SecondaryButton
                    title="Edit"
                    size="tiny"
                    data-testid="agent-table-action-renderer-edit-button"
                    onClick={() => onLinkClick(`/distribution/agents/${data.id}`)}
                >
                    <PenIcon />
                </SecondaryButton>
            ) : null}
            {canArchiveAgent ? (
                hasChildren ? (
                    isArchived ? (
                        <span>
                            <SecondaryButton
                                title="Unarchive"
                                size="tiny"
                                data-testid="agent-table-action-renderer-archive-button"
                                onClick={() => setShowConfirmationModal(true)}
                            >
                                <ArrowCcwIcon />
                            </SecondaryButton>
                            <PromptModal
                                isOpen={showConfirmationModal}
                                title="Unarchive Agent"
                                onConfirm={() => {
                                    handleArchive(isArchived ? AffiliateStatus.ACTIVE : AffiliateStatus.INACTIVE);
                                    setShowConfirmationModal(false);
                                }}
                                onClose={() => setShowConfirmationModal(false)}
                            >
                                <div>
                                    Are you sure you want to unarchive{" "}
                                    <b>
                                        <var data-var="name">{data.name}</var>
                                    </b>
                                    ?
                                </div>
                            </PromptModal>
                        </span>
                    ) : null
                ) : (
                    <span>
                        <SecondaryButton
                            title={isArchived ? "Unarchive" : "Archive"}
                            size="tiny"
                            data-testid="agent-table-action-renderer-archive-button"
                            onClick={() => setShowConfirmationModal(true)}
                        >
                            {isArchived ? <ArrowCcwIcon /> : <ArchiveIcon />}
                        </SecondaryButton>
                        <PromptModal
                            isOpen={showConfirmationModal}
                            title={isArchived ? "Unarchive Agent" : "Archive Agent"}
                            onConfirm={() => {
                                handleArchive(isArchived ? AffiliateStatus.ACTIVE : AffiliateStatus.INACTIVE);
                                setShowConfirmationModal(false);
                            }}
                            onClose={() => setShowConfirmationModal(false)}
                        >
                            <div>
                                Are you sure you want to {isArchived ? "unarchive" : "archive"}{" "}
                                <b>
                                    <var data-var="name">{data.name}</var>
                                </b>
                                ?
                            </div>
                        </PromptModal>
                    </span>
                )
            ) : null}
        </span>
    );
};

interface TermsTemplateCellRendererProps {
    data: CustomAffiliate;
}

const TermsTemplateRenderer = ({ data }: TermsTemplateCellRendererProps) => {
    const { isGranted } = useAuthorization();
    const user = useUser();

    const loggedInAgentOrChild = data.id === user.id;
    const isTermsTemplateAvailable = isGranted(Permission.AGENTS_TERMS_TEMPLATE_ACTIONS) && !loggedInAgentOrChild;

    const { data: appliedTermsTemplates, isLoading } = useCollection<PurchaseRuleLink>("purchaseRuleLinks", {
        "tags._id": "Affiliate",
        "entities.id": data.id,
    });

    const history = useHistory();
    const handleClick = () => {
        if (isTermsTemplateAvailable) {
            const termsUrl = `/distribution/termsTemplates/${appliedTermsTemplates?.[0]?.id}`;

            history.push(termsUrl);
        }
    };

    if (isLoading) {
        return <Spinner />;
    }

    return (
        <NoTranslate className="terms-template-cell text-gray-darker ">
            {appliedTermsTemplates?.[0] ? (
                <span
                    className={clsx(isTermsTemplateAvailable ? "cursor-pointer underline" : "")}
                    onClick={handleClick}
                >
                    {appliedTermsTemplates?.[0]?.name}
                </span>
            ) : (
                <span>-</span>
            )}
        </NoTranslate>
    );
};

interface NameCellRendererProps {
    data: CustomAffiliate;
}

const NameCellRenderer = ({ data }: NameCellRendererProps) => {
    const isChildAgent = !!data?.parent;
    const isInactive = data.status === AffiliateStatus.INACTIVE;

    return (
        <span className="ag-group-value" data-testid="agent-table-name-renderer-expand-button">
            <NoTranslate className={clsx({ "font-bold text-black": !isChildAgent })}>
                <span
                    className={clsx("truncate", {
                        "italic text-gray": isInactive,
                    })}
                >
                    {data?.name}
                </span>
            </NoTranslate>
        </span>
    );
};

const CodeRenderer = ({ data }: { data: Affiliate }) => {
    const isParentAgent = !data?.parent;
    const isInactive = data.status === AffiliateStatus.INACTIVE;
    return (
        <NoTranslate
            className={clsx({ "font-bold text-black": isParentAgent }, { "italic text-gray": isInactive })}
            data-testid="agent-table-code-renderer-field"
        >
            {data.code?.toUpperCase()}
        </NoTranslate>
    );
};

interface CommissionAndDiscountCellRendererProps {
    data: Affiliate;
    lineItemType: LineItemType;
}

const CommissionAndDiscountCellRenderer = ({ data, lineItemType }: CommissionAndDiscountCellRendererProps) => {
    const seller = useSeller();
    const viewRef = useRef(null);
    const [inViewport] = useInViewport(viewRef);

    const tags = { "tags._id": "Affiliate" };

    const { data: termsTemplates } = useAutoFetchCollection<PurchaseRuleLink>(
        "purchaseRuleLinks",
        {
            "entities.id[in]": data.id,
            tags,
        },
        { enabled: inViewport },
    );

    const agentTemplate = useMemo(
        () =>
            termsTemplates?.find((template) =>
                template.entities.some((entity: PurchaseRuleLinkEntity) => entity.id === data.id),
            ),
        [termsTemplates, data.id],
    );

    const mappedIds = useMemo(
        () => agentTemplate?.purchaseRules.map((purchaseRule) => purchaseRule.purchaseRulePermanentId),
        [agentTemplate],
    );

    const { data: purchaseRules, isLoading } = useAutoFetchCollection<PurchaseRule>(
        "purchaseRules",
        {
            "id[in]": mappedIds?.join(","),
        },
        { enabled: inViewport && mappedIds && mappedIds?.length > 0 },
    );

    const value = getAgentTermValue(data.id, seller.currency, lineItemType, agentTemplate, purchaseRules);
    const isInactive = data.status === AffiliateStatus.INACTIVE;
    const numericPart = extractNumericPart(value);
    const isCommission = lineItemType === LineItemType.AFFILIATE_COMMISSION;

    const isShowSpinner = isLoading || !purchaseRules;

    return (
        <NoTranslate
            ref={viewRef}
            className={clsx({ "italic text-gray": isInactive })}
            data-testid="agent-table-discount-commission-value"
        >
            {isShowSpinner ? <Spinner size="small" /> : numericPart || isCommission ? value : "--"}
        </NoTranslate>
    );
};

export const getAgentTermValue = (
    agentId: ID,
    currency: string,
    lineItemType: LineItemType,
    termsTemplates?: PurchaseRuleLink | PurchaseRuleLink[],
    purchaseRules?: PurchaseRule[],
    partnerObject?: PartnerObject,
) => {
    const isCommission = lineItemType === LineItemType.AFFILIATE_COMMISSION;
    if (!termsTemplates || !purchaseRules) {
        return "";
    }

    let localAgentTemplate = termsTemplates;
    let localPurchaseRules = purchaseRules;

    if (Array.isArray(termsTemplates)) {
        const { agentTemplate, agentPurchaseRules } = getPurchaseRuleLinkPurchaseRulesForAgent(
            agentId,
            partnerObject ?? PartnerObject.AFFILIATE,
            termsTemplates,
            purchaseRules,
        );

        localAgentTemplate = agentTemplate!;
        localPurchaseRules = agentPurchaseRules;
    }

    const exceptionPurchaseRules = getPurchaseRulesByTag(TagId.AFFILIATE_OVERRIDE, localPurchaseRules);

    if (exceptionPurchaseRules?.length) {
        return "Varies by product";
    }

    let value = "";

    if (localAgentTemplate) {
        const summary = getSummary(currency, lineItemType, localAgentTemplate as PurchaseRuleLink, localPurchaseRules);
        if (summary) {
            value = summary;
        }
    }

    if (!value && isCommission) {
        value = numberFormat(0, currency);
    }

    return value;
};

export const getSummary = (
    currency: string,
    lineItemType: LineItemType,
    termsTemplate?: PurchaseRuleLink,
    purchaseRules?: PurchaseRule[],
) => {
    if (!termsTemplate || !purchaseRules) {
        return null;
    }

    const termsType = getTermsType(termsTemplate);

    if (termsType === TermsType.ADVANCED) {
        return "Varies by demographic";
    }

    const purchaseRule = purchaseRules.find((purchaseRule) => {
        return (
            purchaseRule.tags?.some((tag) => tag.id === lineItemType) ??
            purchaseRule.name?.startsWith(lineItemType === LineItemType.AFFILIATE_DISCOUNT ? "Discount" : "Commission")
        );
    });

    if (!purchaseRule) {
        return null;
    }

    const amount = Math.abs(getAmount(purchaseRule));

    return getAlgorithm(purchaseRule) === CouponAlgorithm.ABSOLUTE ? numberFormat(amount, currency) : `${amount}%`;
};

const getPurchaseRuleLinkPurchaseRulesForAgent = (
    agentId: ID,
    agentObject: PartnerObject,
    termsTemplates: PurchaseRuleLink[],
    purchaseRules: PurchaseRule[],
) => {
    let agentTemplate = termsTemplates.find((template) =>
        template.entities.some((entity: PurchaseRuleLinkEntity) => entity.id === agentId),
    );
    if (agentObject === PartnerObject.XOLA_PARTNER) {
        agentTemplate = termsTemplates.find((template) => template.id === agentId);
    }

    let agentPurchaseRules: PurchaseRule[] = [];
    if (agentTemplate) {
        agentPurchaseRules = purchaseRules.filter((purchaseRule) =>
            agentTemplate?.purchaseRules.some(
                ({ purchaseRulePermanentId }) => purchaseRule.id === purchaseRulePermanentId,
            ),
        );
    }

    return { agentTemplate, agentPurchaseRules };
};

LicenseManager.setLicenseKey(config.agGrid.key);
